/**
 * Levels-menu settings rows for the volume pane: Vest scale mode and the CME
 * volume-spike rule (N, threshold, baseline) plus whether the "2.4x"
 * multiples are drawn over the spike bars. Number inputs commit on blur /
 * Enter, clamped.
 */
import React, { useEffect, useState } from 'react';
import { useVolumePaneSettingsStore } from '../store/volumePaneSettingsStore';
import type { CmeBaseline } from '../utils/cmeVolumeSpikes';

const rowStyle: React.CSSProperties = { padding: '2px 12px 6px 32px', gap: 10, flexWrap: 'wrap' };
const inputStyle: React.CSSProperties = { width: 52, fontSize: 11, padding: '1px 4px' };
const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 4 };

export const VestScaleRow: React.FC = () => {
  const mode = useVolumePaneSettingsStore((s) => s.vestScale);
  const setMode = useVolumePaneSettingsStore((s) => s.setVestScale);
  return (
    <div
      className="levels-vp-row"
      style={rowStyle}
      data-testid="vest-scale-row"
      title="Independent: Vest bars on their own scale (height relative to Vest's own range). Same units: stacked on the CME bar in chart-contract equivalents (Vest notional / ($ per point x price); MNQ $2, NQ $20)."
    >
      <label className="levels-vp-label" style={labelStyle}>
        Vest scale
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value === 'same' ? 'same' : 'independent')}
          aria-label="Vest volume scale"
          data-testid="vest-scale-select"
          style={{ fontSize: 11 }}
        >
          <option value="independent">independent</option>
          <option value="same">same units</option>
        </select>
      </label>
    </div>
  );
};

export const CmeSpikeSettingsRow: React.FC = () => {
  const cme = useVolumePaneSettingsStore((s) => s.cme);
  const setCme = useVolumePaneSettingsStore((s) => s.setCme);
  const showMultiples = useVolumePaneSettingsStore((s) => s.showSpikeMultiples);
  const setShowMultiples = useVolumePaneSettingsStore((s) => s.setShowSpikeMultiples);
  const [n, setN] = useState(String(cme.lookback));
  const [x, setX] = useState(String(cme.threshold));
  useEffect(() => setN(String(cme.lookback)), [cme.lookback]);
  useEffect(() => setX(String(cme.threshold)), [cme.threshold]);
  const commitN = () => {
    if (n.trim() !== '' && Number.isFinite(Number(n))) setCme({ lookback: Number(n) });
    setN(String(useVolumePaneSettingsStore.getState().cme.lookback));
  };
  const commitX = () => {
    if (x.trim() !== '' && Number.isFinite(Number(x))) setCme({ threshold: Number(x) });
    setX(String(useVolumePaneSettingsStore.getState().cme.threshold));
  };
  return (
    <div
      className="levels-vp-row"
      style={rowStyle}
      data-testid="cme-spike-settings"
      title="CME rel vol = bar volume / baseline, per 2m base bar, closed bars only, 15 min after a session (re)open skipped. Baseline: auto = same time of day over the last 10 sessions when 5+ exist, else the previous N bars."
    >
      <label className="levels-vp-label" style={labelStyle}>
        N
        <input type="number" min={5} max={500} value={n} onChange={(e) => setN(e.target.value)} onBlur={commitN}
          onKeyDown={(e) => e.key === 'Enter' && commitN()} aria-label="CME spike lookback bars" data-testid="cme-spike-lookback" style={inputStyle} />
      </label>
      <label className="levels-vp-label" style={labelStyle}>
        ≥
        <input type="number" min={1.1} max={20} step={0.1} value={x} onChange={(e) => setX(e.target.value)} onBlur={commitX}
          onKeyDown={(e) => e.key === 'Enter' && commitX()} aria-label="CME spike threshold" data-testid="cme-spike-threshold" style={inputStyle} />
        x
      </label>
      <label className="levels-vp-label" style={labelStyle}>
        vs
        <select value={cme.baseline} onChange={(e) => setCme({ baseline: e.target.value as CmeBaseline })} aria-label="CME spike baseline"
          data-testid="cme-spike-baseline" style={{ fontSize: 11 }}>
          <option value="auto">auto</option>
          <option value="tod">time of day</option>
          <option value="trailing">trailing</option>
        </select>
      </label>
      <label
        className="levels-vp-label"
        style={{ ...labelStyle, flexBasis: '100%' }}
        title="Off: no multiple text over spike bars. The brighter bar and the 🐋 stay."
      >
        <input
          type="checkbox"
          checked={showMultiples}
          onChange={(e) => setShowMultiples(e.target.checked)}
          aria-label="Show spike multiples"
          data-testid="cme-spike-multiples"
        />
        Show spike multiples (2.4x labels)
      </label>
    </div>
  );
};
